import React, { useState } from "react";
import ConsultationForm from "./Consultation"; // adjust path if your pages folder is elsewhere
import Maternity1 from "../assets/images/Maternity/Maternity-1.avif";
import Maternity2 from "../assets/images/Maternity/Maternity-2.png";
import Maternity3 from "../assets/images/Maternity/Maternity-3.avif";
import Maternity4 from "../assets/images/Maternity/Maternity-4.jpg";
import Maternity5 from "../assets/images/Maternity/Maternity-5.png";
import Maternity6 from "../assets/images/Maternity/Maternity-6.jpg";
import Maternity7 from "../assets/images/Maternity/Maternity-7.jpg";
import Maternity8 from "../assets/images/Maternity/Maternity-8.jpg";
import Maternity9 from "../assets/images/Maternity/Maternity-9.jpg";
import SEO from "../components/SEO";

/**
 * Maternity Suite Designs — landing page
 * Same design system as OTSuiteDesigns.jsx — black/white/grey/ash palette,
 * spec-line cards (Zone / Size / Suite Type),
 * "Get Free Quote" opens the shared Consultation modal.
 */

const FONT_IMPORT_URL =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";

const tokens = {
  ink: "#343F86",
  bg: "#ECEEF6",
  card: "#FFFFFF",
  teal: "#3A4060",
  tealDark: "#262E63",
  amber: "#8A8F9C",
  blue: "#5B6072",
  hairline: "#CDD1E0",
  sub: "#5B6072",
};

const maternitySuites = [
  {
    name: "Labour, Delivery & Recovery (LDR) Suite",
    zone: "Level 1",
    size: "26 sq.m",
    spec: "LDR",
    tag: "LDR",
    note: "Birthing bed with adjustable positions, infant warmer bay, dimmable mood lighting.",
    image: Maternity1,
  },
  {
    name: "Antenatal Assessment Room",
    zone: "Ground Floor",
    size: "16 sq.m",
    spec: "Antenatal",
    tag: "Antenatal",
    note: "Fetal monitoring station, comfortable exam couch, ultrasound-ready power points.",
    image: Maternity2,
  },
  {
    name: "Postnatal Rooming-In Ward",
    zone: "Level 1",
    size: "24 sq.m / 2 beds",
    spec: "Postnatal",
    tag: "Postnatal",
    note: "Bassinet beside every bed, breastfeeding privacy screen, soft ambient lighting.",
    image: Maternity3,
  },
  {
    name: "High-Risk Maternity Suite",
    zone: "Level 2",
    size: "28 sq.m",
    spec: "High-Risk",
    tag: "High-Risk",
    note: "Continuous fetal monitoring, direct OT corridor link, dedicated emergency response bay.",
    image: Maternity4,
  },
  {
    name: "Operative Delivery (C-Section) OT",
    zone: "Level 1",
    size: "32 sq.m",
    spec: "Caesarean",
    tag: "Operative",
    note: "Dedicated obstetric OT, neonatal resuscitation corner, direct recovery-room access.",
    image: Maternity5,
  },
  {
    name: "NICU-Linked Maternity Suite",
    zone: "Level 1",
    size: "30 sq.m",
    spec: "NICU-Linked",
    tag: "NICU-Linked",
    note: "Direct corridor to NICU, kangaroo-care seating, rooming-in transition support.",
    image: Maternity6,
  },
  {
    name: "Premium Birthing Suite",
    zone: "Level 2",
    size: "40 sq.m",
    spec: "Premium",
    tag: "Premium",
    note: "Lounge-style birthing room, attached family suite, premium finishes and amenities.",
    image: Maternity7,
  },
  {
    name: "Lactation & Counseling Room",
    zone: "Level 1",
    size: "14 sq.m",
    spec: "Lactation Support",
    tag: "Lactation",
    note: "Private nursing pods, lactation consultant desk, comfortable recliners for mothers.",
    image: Maternity8,
  },
  {
    name: "Multi-Bed Maternity Ward Cluster",
    zone: "Level 1",
    size: "90 sq.m / 8 beds",
    spec: "General Maternity",
    tag: "Multi-Bed",
    note: "Central nursing station sightlines, shared baby-care nursery, visitor lounge zone.",
    image: Maternity9,
  },
];

function SpecLine({ zone, size, spec }) {
  return (
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11.5,
        letterSpacing: 0.3,
        color: tokens.sub,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>ZONE&nbsp;<b style={{ color: tokens.teal }}>{zone}</b></span>
      <span>·</span>
      <span>SIZE&nbsp;<b style={{ color: tokens.ink }}>{size}</b></span>
      <span>·</span>
      <span>{spec}</span>
    </div>
  );
}

function MaternitySuiteCard({ item, onGetQuote }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: tokens.card,
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${tokens.hairline}`,
        boxShadow: hover
          ? "0 14px 28px -14px rgba(14,124,123,0.35)"
          : "0 2px 6px -2px rgba(43,58,66,0.08)",
        transform: hover ? "translateY(-3px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      {/* corner bracket signature */}
      <div
        style={{
          position: "absolute", top: 10, left: 10, width: 22, height: 22,
          borderTop: `2px solid ${tokens.teal}`, borderLeft: `2px solid ${tokens.teal}`,
          zIndex: 2,
        }}
      />
      <div style={{ height: 168, position: "relative", background: tokens.tealDark }}>
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%", height: "100%", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.55)",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: 0.5,
            }}
          >
            ADD IMAGE
          </div>
        )}
        {/* tag chip */}
        <span
          style={{
            position: "absolute", bottom: 10, right: 12,
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            color: "#fff", background: "rgba(0,0,0,0.45)",
            padding: "2px 8px", borderRadius: 2,
          }}
        >
          {item.tag.toUpperCase()}
        </span>
      </div>
      <div style={{ padding: "16px 16px 18px" }}>
        <h3
          style={{
            fontFamily: "'Montserrat', 'Inter', sans-serif", fontWeight: 600, fontSize: 17.5,
            color: tokens.ink, margin: "0 0 6px", lineHeight: 1.25,
          }}
        >
          {item.name}
        </h3>
        <p style={{ fontSize: 13, color: tokens.sub, margin: "0 0 10px", lineHeight: 1.45 }}>
          {item.note}
        </p>
        <SpecLine zone={item.zone} size={item.size} spec={item.spec} />
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => onGetQuote(item)}
            style={{
              flex: 1, padding: "9px 10px", fontSize: 12.5, fontWeight: 600,
              fontFamily: "'Montserrat', 'Inter', sans-serif", letterSpacing: 0.2,
              background: tokens.amber, color: tokens.ink,
              border: "none", borderRadius: 3, cursor: "pointer",
            }}
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MaternitySuiteDesigns() {
  const [showConsultation, setShowConsultation] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleGetQuote = (item) => {
    setSelectedItem(item);
    setShowConsultation(true);
  };

  return (
     <>
    <SEO/>
    <div
      style={{
        background: tokens.bg,
        color: tokens.ink,
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`@import url('${FONT_IMPORT_URL}');
        * { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        button:focus-visible, a:focus-visible { outline: 2px solid ${tokens.teal}; outline-offset: 2px; }
        .mat-grid-3x3 { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 900px) { .mat-grid-3x3 { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .mat-grid-3x3 { grid-template-columns: 1fr; } }
        .mat-modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center;
          padding: 1rem;
        }
        .mat-modal-inner {
          position: relative; width: 100%; max-width: 680px;
          max-height: 90vh; overflow-y: auto; border-radius: 18px;
        }
        .mat-modal-close {
          position: absolute; top: 14px; right: 16px; z-index: 10;
          background: #262E63; border: none; color: #fff;
          width: 32px; height: 32px; border-radius: 50%;
          font-size: 20px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          line-height: 1;
        }
      `}</style>

      {/* spacer so the fixed/sticky page navbar in the host app doesn't overlap the grid */}
      <div style={{ height: 32 }} />

      {/* GRID */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 10px" }}>
        <div
          className="mat-grid-3x3"
          style={{
            display: "grid",
            gap: 22,
            marginTop: 70,
          }}
        >
          {maternitySuites.map((item, i) => (
            <MaternitySuiteCard key={i} item={item} onGetQuote={handleGetQuote} />
          ))}
        </div>
      </section>

      {/* Consultation Modal — opens on "Get Free Quote" click */}
      {showConsultation && (
        <div
          className="mat-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConsultation(false); }}
        >
          <div className="mat-modal-inner">
            <button
              className="mat-modal-close"
              onClick={() => setShowConsultation(false)}
              aria-label="Close consultation form"
            >
              ×
            </button>
            <ConsultationForm suite={selectedItem} />
          </div>
        </div>
      )}
    </div>
    </>
  );
}